import { useEffect, useState } from 'react';
import { fetchSettings } from './db';
import type { SiteSettings } from './types';

export const STORAGE_KEY = 'kisapuk-site-settings';
export const SETTINGS_UPDATED_EVENT = 'kisapuk-site-settings-updated';
export const ADMIN_CONTENT_UPDATED_EVENT = 'kisapuk-admin-content-updated';

export function readCachedSettings(): SiteSettings {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function cacheSettings(settings: SiteSettings) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function notifySiteSettingsUpdated(settings?: SiteSettings) {
  if (typeof window === 'undefined') return;
  if (settings) cacheSettings(settings);
  window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED_EVENT, { detail: settings || readCachedSettings() }));
}

export function notifyAdminContentUpdated() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(ADMIN_CONTENT_UPDATED_EVENT));
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(readCachedSettings());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function syncSettings() {
      setLoading(true);
      try {
        const fresh = await fetchSettings();
        if (!active) return;
        setSettings(fresh);
        cacheSettings(fresh);
      } catch {
        if (!active) return;
        const cached = readCachedSettings();
        if (Object.keys(cached).length) {
          setSettings(cached);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    const handleUpdated = (event: Event) => {
      const detail = (event as CustomEvent<SiteSettings>).detail;
      if (detail) {
        setSettings(detail);
        cacheSettings(detail);
      } else {
        void syncSettings();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          setSettings(JSON.parse(event.newValue));
        } catch {
          // ignore invalid cache payload
        }
      }
    };

    void syncSettings();
    window.addEventListener(SETTINGS_UPDATED_EVENT, handleUpdated as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      active = false;
      window.removeEventListener(SETTINGS_UPDATED_EVENT, handleUpdated as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return { settings, loading };
}
